import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "className", "standalone", "trailing", "use", "setStyledClassName", "$cssStyles", "renderGripOnHover"],
  _excluded2 = ["children", "position", "size", "use"];
import { cloneElement, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { toShorthandSize } from '../../utils/tShirtSizes';
import { warnIfFragment } from '../../utils/warnIfFragment/warnIfFragment';
import styled from 'foundations-theming/utils';
import { filterProps } from '../../../utils/filterProps';
import { ChildrenWrapper } from '../ChildrenWrapper/ChildrenWrapper';
import { findDOMNode } from 'react-dom';
import { useDragHandleHoverContainerContext } from '../DragHandleHoverContainer/DragHandleHoverContainerProvider';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { isCanvasTheme } from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const emptyBox = /*#__PURE__*/_jsx("span", {});
const maxHeightForSize = {
  sm: 10,
  md: 50,
  lg: 90
};
const CANVAS_OFFSET = '9px';
const TRELLIS_OFFSET = '4px';
const CANVAS_SVG_PATH = 'M0 0h2v2H0V0zm0 8h2v2H0V8zm0 8h2v2H0v-2zM0 4h2v2H0V4zm0 8h2v2H0v-2zm0 8h2v2H0v-2zM3 0h2v2H3V0zm0 8h2v2H3V8zm0 8h2v2H3v-2zM3 4h2v2H3V4zm0 8h2v2H3v-2zm0 8h2v2H3v-2z';
const TRELLIS_SVG_PATH = '<circle cx="10.14" cy="4.14" r="1.14" /><circle cx="10.14" cy="4.14" r="2.29" /><circle cx="10.14" cy="15.57" r="1.14" /><path d="M10.14 17.86c-1.26 0-2.29-1.03-2.29-2.29s1.03-2.29 2.29-2.29 2.29 1.03 2.29 2.29-1.03 2.29-2.29 2.29" /><circle cx="10.14" cy="27" r="1.14" /><circle cx="10.14" cy="27" r="2.29" /><circle cx="21.57" cy="4.14" r="1.14" /><path d="M21.57 6.43c-1.26 0-2.29-1.03-2.29-2.29s1.03-2.29 2.29-2.29 2.29 1.03 2.29 2.29-1.03 2.29-2.29 2.29m0-2.29" /><circle cx="21.57" cy="15.57" r="1.14" /><path d="M21.57 17.86c-1.26 0-2.29-1.03-2.29-2.29s1.03-2.29 2.29-2.29 2.29 1.03 2.29 2.29-1.03 2.29-2.29 2.29" /><circle cx="21.57" cy="27" r="1.14" /><path d="M21.57 29.29c-1.26 0-2.29-1.03-2.29-2.29s1.03-2.29 2.29-2.29 2.29 1.03 2.29 2.29-1.03 2.29-2.29 2.29m0-2.29" />';
const isSmallSize = size => size === 'sm' || size === 'small';
const getFill = (use, theme) => use === 'on-dark' ? theme.color['icon-core-on-fill-default'] : theme.color['icon-tertiary-default'];
const calculateOffset = (standalone, trailing, baseOffset) => {
  const offsetX = standalone ? trailing ? '0' : '50%' : baseOffset;
  return trailing && !standalone ? `calc(100% - ${offsetX})` : offsetX;
};

// Canvas theme: original simple logic (no size variations)
const getCanvasGrippieBackground = ({
  use,
  theme,
  standalone,
  trailing
}) => {
  const fill = getFill(use, theme);
  const width = '5px';
  const height = '24px';
  const viewBox = '0 0 5 24';
  const finalOffsetX = calculateOffset(standalone, trailing, CANVAS_OFFSET);
  const svgStr = `<svg width='${width}' height='${height}' xmlns='http://www.w3.org/2000/svg' viewBox='${viewBox}' fill='${fill}'><path d='${CANVAS_SVG_PATH}'/></svg>`;
  return {
    width,
    height,
    svgStr,
    backgroundPosition: `${finalOffsetX} calc(50% + 1px)`,
    beforePseudoElementOffset: CANVAS_OFFSET
  };
};
const getTrellisGrippieBackground = ({
  use,
  theme,
  size,
  standalone,
  trailing
}) => {
  const fill = getFill(use, theme);
  const isSmall = isSmallSize(size);
  const width = '16px';
  const height = '16px';
  const iconWidth = isSmall ? '10px' : width;
  const iconHeight = isSmall ? '10px' : height;
  // Small icons use 32x32, regular icons use 32x35.5 to prevent pattern clipping when repeated at certain sizes
  const viewBox = isSmall ? '0 0 32 32' : '0 0 32 35.5';
  const finalOffsetX = calculateOffset(standalone, trailing, TRELLIS_OFFSET);
  const svgStr = `<svg xmlns="http://www.w3.org/2000/svg" width='${iconWidth}' height='${iconHeight}' viewBox="${viewBox}" fill='${fill}'>${TRELLIS_SVG_PATH}</svg>`;
  return {
    width,
    height,
    svgStr,
    backgroundPosition: `${finalOffsetX} center`,
    beforePseudoElementOffset: TRELLIS_OFFSET
  };
};
const DragHandleChild = styled(_ref => {
  let {
      children,
      className,
      setStyledClassName
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  useEffect(() => {
    setStyledClassName(className);
  }, [className, setStyledClassName]);
  return /*#__PURE__*/cloneElement(children, Object.assign({}, rest));
})(({
  size,
  standalone,
  trailing,
  use,
  renderGripOnHover,
  theme,
  $cssStyles
}) => {
  const grippieBackground = isCanvasTheme(theme) ? getCanvasGrippieBackground({
    use,
    theme,
    standalone,
    trailing
  }) : getTrellisGrippieBackground({
    use,
    theme,
    size,
    standalone,
    trailing
  });
  const backgroundImageUrl = `url("data:image/svg+xml,${encodeURIComponent(grippieBackground.svgStr)}")`;
  return Object.assign({}, !renderGripOnHover ? {
    backgroundImage: backgroundImageUrl
  } : {
    ['&:hover']: {
      backgroundImage: backgroundImageUrl
    }
  }, {
    backgroundPosition: grippieBackground.backgroundPosition,
    backgroundRepeat: 'no-repeat',
    cursor: 'grab'
  }, standalone && {
    display: 'inline-block',
    lineHeight: theme.typography['body-100'].lineHeight,
    minWidth: grippieBackground.width,
    /* By using hidden content instead of fixed height, we inherit line-height, vertical-align, etc. */
    ['&::after']: {
      content: '"."',
      visibility: 'hidden'
    }
  }, size !== 'default' && {
    backgroundImage: 'none !important',
    position: 'relative',
    '&::before': {
      content: `""`,
      backgroundImage: backgroundImageUrl,
      backgroundRepeat: 'repeat-y',
      position: 'absolute',
      [trailing ? 'right' : 'left']: grippieBackground.beforePseudoElementOffset,
      top: '50%',
      transform: 'translateY(-50%)',
      width: grippieBackground.width,
      height: `calc(100% - ${grippieBackground.height})`,
      maxHeight: `${maxHeightForSize[size]}px`,
      margin: 0
    }
  }, $cssStyles && $cssStyles);
});
export const DragHandle = _ref2 => {
  let {
      children = emptyBox,
      position = 'start',
      size = 'default',
      use = 'default'
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const [styledClassName, setStyledClassName] = useState('');
  const [targetClassName, setTargetClassName] = useState(null);
  const [targetElement, setTargetElement] = useState(null);
  const lastAddedClassRef = useRef(null);
  warnIfFragment(children, 'DragHandle');
  useLayoutEffect(() => {
    if (!targetElement) return undefined;
    const observer = new MutationObserver(() => {
      setTargetClassName(targetElement.className);
    });

    // Observer configuration: observe changes in className attribute as changes
    // in focus state can change styled-components className. If there are changes
    // we need a way to track that and re-trigger the layout effect to re-apply
    // the original className that contains the drag handle styling
    observer.observe(targetElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    return () => {
      observer.disconnect();
    };
  }, [targetElement]);

  // this is definitely weird & less than ideal, but the tl;dr here is:
  // 1. we can't pass the generated styled-components classname to the styled(Component) if Component is a
  // transitional component that doesn't accept a classname prop
  // 2. we also don't want to add forwardRef to _every_ component that could be a child of DragHandle (which is anything)
  // 3. so use findDOMNode to get a ref to the underlying wrapped/child component
  // 4. we then use a callback above to pass the generated styled-components classname back into this component
  // 5. from there, we can manually add the generated classname to the childrenWrapperRef
  useLayoutEffect(() => {
    if (targetElement && styledClassName) {
      const newClass = styledClassName.split(' ')[1];

      // Remove the previously added class if it exists
      if (lastAddedClassRef.current) {
        targetElement.classList.remove(lastAddedClassRef.current);
      }

      // Add the new class
      if (newClass) {
        targetElement.classList.add(newClass);
        lastAddedClassRef.current = newClass;
      }
    }
  }, [targetElement, styledClassName, targetClassName]);
  const {
    renderGripOnHover
  } = useDragHandleHoverContainerContext();
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(ChildrenWrapper
    // @ts-expect-error - incompatible ref type
    , {
      ref: childNode => setTargetElement(
      // eslint-disable-next-line react/no-find-dom-node
      findDOMNode(childNode)),
      children: /*#__PURE__*/_jsx(DragHandleChild, Object.assign({}, domProps, {
        $cssStyles: cssStyles,
        size: toShorthandSize(size),
        standalone: children === emptyBox,
        trailing: position === 'end',
        use: use,
        setStyledClassName: setStyledClassName,
        renderGripOnHover: renderGripOnHover,
        children: children
      }))
    })
  });
};
DragHandle.displayName = 'DragHandle';